import React from 'react';
import PropTypes from 'prop-types';
import FixedCircularProgress from 'components/FixedCircularProgress';

import { Edge, Point, Node } from '../Structures';
import config from '../config';

import GraphView from '../components/GraphView';


class GraphViewController extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      foldedEdgesIds: [],
    };
  }

  parseData = (nodesData, edgesData, rootNodeId) => {
    const { foldedEdgesIds } = this.state;

    const nodes = [];
    const edges = [];

    for (const nodeData of nodesData) {
      const node = new Node(nodeData);
      for (const edgeData of nodeData.buttons) {
        const edge = new Edge(edgeData.id, edgeData.text, node);
        node.addEdge(edge);
        edges.push(edge);
      }
      nodes.push(node);
    }

    for (const connection of edgesData) {
      const edge = edges.find(edge => edge.id === connection.buttonId);
      const destination = nodes.find(node => node.id === connection.questionId);
      if (edge && destination) {
        edge.destination = destination;
      }

      if (foldedEdgesIds.includes(connection.buttonId)) {
        edge.folded = true;
        edge.display = false;
      }
    }

    const rootNode = nodes.find(node => node.id === rootNodeId);

    const reachableNodes = GraphViewController.getReachableNodes(rootNode);
    const reachableNodesIds = reachableNodes.map(node => node.id);
    const reachableNodesEdges = reachableNodes.reduce((accumulatedEdges, node) => accumulatedEdges.concat(node.edges), []);
    reachableNodesEdges.forEach((edge) => {
      if (edge.destination && !reachableNodesIds.includes(edge.destination.id)) {
        edge.display = false;
      }
    });

    return {
      nodes: reachableNodes,
      edges: reachableNodesEdges,
    };
  };

  // node should not be folded for only at least one way
  static getReachableNodes(rootNode) {
    const nodes = [];
    const allNodes = [];
    const alreadyParsedNodesIds = [];

    nodes.push(rootNode);
    alreadyParsedNodesIds.push(rootNode.id);
    while ((nodes.length)) {
      const currentNode = nodes.shift();
      allNodes.push(currentNode);

      for (const edge of currentNode.edges) {
        if (!edge.destination || edge.folded) {
          continue;
        }

        const destination = edge.destination;
        if (!alreadyParsedNodesIds.includes(destination.id)) {
          nodes.push(destination);
          alreadyParsedNodesIds.push(destination.id);
        }
      }
    }

    return allNodes;
  }

  static drawGraph(nodes, edges, rootNodeId) {
    const rootNode = nodes.find(node => node.id === rootNodeId);

    GraphViewController.cleanNodePositionData(nodes);
    GraphViewController.calculateNodesHeights(nodes);
    GraphViewController.calculateNodesIndexes(rootNode, nodes);
    GraphViewController.putNodesAttachmentsIncludedTypes(nodes);
    GraphViewController.calculateGraphElementsPositions(nodes);
    GraphViewController.createGraphEdgesShapesPoints(nodes, edges);
  }

  static cleanNodePositionData = (nodes) => {
    nodes.forEach(node => node.cleanNodePositionData());
  };

  static calculateNodesHeights(nodes) {
    nodes.forEach((currentNode) => {
      if (currentNode.temporary) {
        currentNode.height = config.sizes.temporaryNodeHeight;
      } else {
        const edgesLength = currentNode.edges.length + currentNode.hasEndEdge;
        const chatWithOperatorLengths = currentNode.data.chatWithOperator * config.sizes.edgeLabelBoxHeight;
        currentNode.height = config.sizes.nodeHeaderHeight + chatWithOperatorLengths + edgesLength * config.sizes.edgeLabelBoxHeight;
        if (currentNode.data.botAttachments && currentNode.data.botAttachments.length) {
          currentNode.height += config.sizes.attachmentBoxSize;
        }
      }
    });
  }

  static calculateNodesIndexes(rootNode, nodes) {
    const parsedNodes = [];
    parsedNodes.push(rootNode);

    rootNode.layerIndex = 0;
    rootNode.verticalIndex = 0;

    while (parsedNodes.length) {
      const currentNode = parsedNodes.shift();

      for (const edge of currentNode.edges) {
        if (!edge.destination || edge.destination.layerIndex !== -1) {
          continue;
        }

        const lastInsertedNode = parsedNodes.length ? parsedNodes[parsedNodes.length - 1] : currentNode;
        parsedNodes.push(edge.destination);

        edge.destination.layerIndex = currentNode.layerIndex + 1;
        edge.destination.verticalIndex = 0;
        if (edge.destination.layerIndex === lastInsertedNode.layerIndex) {
          edge.destination.verticalIndex = lastInsertedNode.verticalIndex + 1;
        }
      }
    }

    let startHorizontalIndex = 0;
    const nodesByLayers = GraphViewController.splitNodesIntoLayers(nodes);

    for (let i = 0; i < nodesByLayers.length; ++i) {
      GraphViewController.calculateLayerNodesPositions(nodesByLayers[i], startHorizontalIndex);
      let layerLastHorizontalIndex = -1;
      nodesByLayers[i].forEach((node) => {
        if (node.horizontalIndex > layerLastHorizontalIndex) {
          layerLastHorizontalIndex = node.horizontalIndex;
        }
      });

      startHorizontalIndex = layerLastHorizontalIndex + 1;
    }
  }

  static splitNodesIntoLayers(nodes) {
    const layersCount = GraphViewController.getLayersCount(nodes);
    const splitedNodes = new Array(layersCount);
    for (let i = 0; i < layersCount; ++i) {
      splitedNodes[i] = nodes.filter(node => node.layerIndex === i);
    }

    return splitedNodes;
  }

  static getLayersCount(nodes) {
    let count = 0;
    for (const node of nodes) {
      if (node.layerIndex > count) {
        count = node.layerIndex;
      }
    }
    count += 1;
    return count;
  }

  static calculateLayerNodesPositions(nodes, startHorizontalIndex) {
    let currentColumnHeight = 0;
    let currentVerticalIndex = 0;
    let currentHorizontalIndex = startHorizontalIndex;

    for (const node of nodes) {
      node.verticalIndex = currentVerticalIndex;
      currentVerticalIndex += 1;

      node.horizontalIndex = currentHorizontalIndex;

      currentColumnHeight += node.height + config.sizes.nodesVerticalSpacing;
      if (currentColumnHeight >= config.sizes.columnMaxHeight) {
        currentColumnHeight = 0;
        currentHorizontalIndex += 1;
        currentVerticalIndex = 0;
      }
    }
  }

  static putNodesAttachmentsIncludedTypes(nodes) {
    nodes.forEach((node) => {
      node.putAttachmentsIncludedTypes();
    });
  }

  static calculateGraphElementsPositions(nodes) {
    GraphViewController.calculateNodesHorizontalPosition(nodes);
    GraphViewController.calculateNodesVerticalPosition(nodes);
    GraphViewController.calculateEdgesLabelsPosition(nodes);
  }

  static calculateNodesHorizontalPosition(nodes) {
    const padding = config.sizes.graphWindowLeftPadding;
    const columnSize = config.sizes.columnSize;

    nodes.forEach((node) => {
      node.x = padding + node.horizontalIndex * columnSize + node.layerIndex * config.sizes.layerSpacing;
    });
  }

  static calculateNodesVerticalPosition(nodes) {
    const columnsHeight = GraphViewController.getColumnsHeight(nodes);

    const maxHeight = Math.max(...columnsHeight);
    const columnsPadding = [];

    for (let i = 0; i < columnsHeight.length; ++i) {
      columnsPadding[i] = (maxHeight - columnsHeight[i]) / 2;
    }

    for (const node of nodes) {
      GraphViewController.calculateSingleNodeVerticalPosition(node, nodes, columnsPadding);
    }
  }

  static calculateSingleNodeVerticalPosition(node, nodes, columnsPadding) {
    const verticalSpacing = config.sizes.nodesVerticalSpacing;
    let y = verticalSpacing;
    y += columnsPadding[node.horizontalIndex];

    nodes.forEach((otherNode) => {
      if (otherNode.horizontalIndex === node.horizontalIndex && otherNode.verticalIndex < node.verticalIndex) {
        y += otherNode.height + verticalSpacing;
      }
    });
    node.y = y;
  }

  static getColumnsHeight(nodes) {
    const columnsNumber = GraphViewController.getColumnsCount(nodes);
    const columnsHeight = [];
    for (let i = 0; i < columnsNumber; ++i) {
      columnsHeight[i] = GraphViewController.getSingleColumnHeight(i, nodes);
    }

    return columnsHeight;
  }

  static getSingleColumnHeight(columnIndex, nodes) {
    const verticalSpacing = config.sizes.nodesVerticalSpacing;
    let height = verticalSpacing;
    nodes.forEach((node) => {
      if (node.horizontalIndex === columnIndex) {
        height += node.height + verticalSpacing;
      }
    });
    return height;
  }

  static getColumnsCount(nodes) {
    let columnsNumber = 1;
    nodes.forEach((node) => {
      if (node.horizontalIndex + 1 > columnsNumber) {
        columnsNumber = node.horizontalIndex + 1;
      }
    });

    return columnsNumber;
  }

  static calculateEdgesLabelsPosition(nodes) {
    nodes.forEach(GraphViewController.calculateNodeEdgesLabelsPosition);
  }

  static calculateNodeEdgesLabelsPosition(node) {
    for (let i = 0; i < node.edges.length; ++i) {
      node.edges[i].x = 0;
      node.edges[i].y = i * config.sizes.edgeLabelBoxHeight;
    }
  }

  static createGraphEdgesShapesPoints(nodes, edges) {
    for (const edge of edges) {
      GraphViewController.createNodeEdgesShapesPoints(edge, nodes);
    }
  }

  static createNodeEdgesShapesPoints(edge, nodes) {
    edge.cleanShapePoints();
    if (!edge.destination || !edge.display) {
      return;
    }

    const destination = edge.destination;
    const source = edge.source;

    if (source.horizontalIndex < destination.horizontalIndex) {
      GraphViewController.createForwardEdgeShapePoints(edge, nodes);
    } else if (source.horizontalIndex > destination.horizontalIndex) {
      GraphViewController.createBackwardEdgeShapePoints(edge, nodes);
    } else {
      GraphViewController.createSameLevelEdgeShapePoints(edge, nodes);
    }

    edge.cleanDuplicateShapesPoints();
  }

  static createForwardEdgeShapePoints(edge, nodes) {
    const source = edge.source;
    const destination = edge.destination;

    // first point going from answer box

    let x = source.x + config.sizes.nodeWidth - config.sizes.nodeInnerPadding;
    let y = source.y + config.sizes.nodeHeaderHeight + edge.y + config.sizes.edgeLabelInnerBoxHeight / 2;
    let point = new Point(x, y);
    edge.addShapePoint(point);

    x = GraphViewController.getMiddlePointToNextColumn(nodes, source.horizontalIndex);
    point = new Point(x, y);
    edge.addShapePoint(point);

    for (let i = source.horizontalIndex + 1; i < destination.horizontalIndex; ++i) {
      const nearestEmptyY = GraphViewController.nearestEmptyAxisY(point.y, i, nodes);
      point = new Point(x, nearestEmptyY);
      edge.addShapePoint(point);
      x = GraphViewController.getMiddlePointToNextColumn(nodes, i);
      point = new Point(x, nearestEmptyY);
      edge.addShapePoint(point);
    }

    y = destination.y + config.sizes.edgeOffsetInNode;
    point = new Point(x, y);
    edge.addShapePoint(point);

    x = destination.x;
    point = new Point(x, y);
    edge.addShapePoint(point);
  }

  static createBackwardEdgeShapePoints(edge, nodes) {
    const source = edge.source;
    const destination = edge.destination;

    let x = source.x + config.sizes.nodeInnerPadding;
    let y = source.y + config.sizes.nodeHeaderHeight + edge.y + config.sizes.edgeLabelInnerBoxHeight / 2;
    let point = new Point(x, y);
    edge.addShapePoint(point);

    x = GraphViewController.getMiddlePointToNextColumn(nodes, source.horizontalIndex - 1);
    point = new Point(x, y);
    edge.addShapePoint(point);

    for (let i = source.horizontalIndex - 1; i > destination.horizontalIndex; --i) {
      y = GraphViewController.nearestEmptyAxisY(point.y, i, nodes);

      point = new Point(x, y);
      edge.addShapePoint(point);
      x = GraphViewController.getMiddlePointToNextColumn(nodes, i - 1);
      point = new Point(x, y);
      edge.addShapePoint(point);
    }

    x = destination.x + config.sizes.nodeWidth + config.sizes.nodeHorizontalSpacing / 2;
    y = destination.y + config.sizes.edgeOffsetInNode;
    point = new Point(x, y);
    edge.addShapePoint(point);

    x -= config.sizes.nodeHorizontalSpacing / 2;
    point = new Point(x, y);
    edge.addShapePoint(point);
  }

  static createSameLevelEdgeShapePoints(edge, nodes) {
    const source = edge.source;
    const destination = edge.destination;

    // first point going from answer box
    let x = source.x + config.sizes.nodeWidth - config.sizes.nodeInnerPadding;
    let y = source.y + config.sizes.nodeHeaderHeight + edge.y + config.sizes.edgeLabelInnerBoxHeight / 2;
    let point = new Point(x, y);
    edge.addShapePoint(point);

    x = GraphViewController.getMiddlePointToNextColumn(nodes, source.horizontalIndex);
    point = new Point(x, y);
    edge.addShapePoint(point);

    y = destination.y + config.sizes.edgeOffsetInNode;
    point = new Point(x, y);
    edge.addShapePoint(point);

    x = destination.x + config.sizes.nodeWidth;
    point = new Point(x, y);
    edge.addShapePoint(point);
  }

  static getMiddlePointToNextColumn(nodes, columnIndex) {
    let columnStartX = null;
    let nextColumnStartX = null;

    for (const node of nodes) {
      if (columnIndex === node.horizontalIndex) {
        columnStartX = node.x;
      }

      if (columnIndex + 1 === node.horizontalIndex) {
        nextColumnStartX = node.x;
      }
    }

    if (nextColumnStartX === null) {
      return columnStartX + config.sizes.nodeWidth + config.sizes.nodeHorizontalSpacing / 2;
    }

    return (columnStartX + config.sizes.nodeWidth + nextColumnStartX) / 2;
  }

  static nearestEmptyAxisY(y, columnNumber, nodes) {
    const columnNodes = nodes.filter(node => node.horizontalIndex === columnNumber);

    columnNodes.sort((firstNode, secondNode) => firstNode.verticalIndex - secondNode.verticalIndex);

    const differences = [];
    for (const node of columnNodes) {
      differences.push(y - node.y);
    }

    const columnLastNode = columnNodes[columnNodes.length - 1];
    differences.push(y - (columnLastNode.y + columnLastNode.height + config.sizes.nodesVerticalSpacing));

    let min = Number.POSITIVE_INFINITY;

    for (const difference of differences) {
      if (Math.abs(difference) < Math.abs(min)) {
        min = difference;
      }
    }

    const nearestEmptyY = y - min - config.sizes.nodesVerticalSpacing / 2;

    return nearestEmptyY;
  }

  static getLayersSeparatorLinesAxises(nodes) {
    const splittedNodesList = GraphViewController.splitNodesIntoLayers(nodes);
    const axisesArray = [];

    for (let i = 1; i < splittedNodesList.length; ++i) {
      const currentLayerNodes = splittedNodesList[i];
      let layerStartX = null;
      currentLayerNodes.forEach((node) => {
        if (layerStartX == null || layerStartX > node.x) {
          layerStartX = node.x;
        }
      });
      const axis = layerStartX - (config.sizes.layerSpacing + config.sizes.nodeHorizontalSpacing) / 2;
      axisesArray.push(axis);
    }

    return axisesArray;
  }

  toggleEdgeFoldState = (edge) => {
    const { foldedEdgesIds } = this.state;
    if (!foldedEdgesIds.includes(edge.id)) {
      this.setState({
        foldedEdgesIds: [...foldedEdgesIds, edge.id],
      });
    } else {
      this.setState({
        foldedEdgesIds: foldedEdgesIds.filter(edgeId => edgeId !== edge.id),
      });
    }
  };

  render() {
    const {
      nodesData, edgesData, selectNode, cleanSelectedNode, addTemporaryNode,
      cleanTemporaryNodes, connectButtonToDestination, readonly, rootNodeId
    } = this.props;
    const { foldedEdgesIds } = this.state;

    if (nodesData.length === 0) {
      return (
        <FixedCircularProgress />
      );
    }

    const { nodes, edges } = this.parseData(nodesData, edgesData, rootNodeId);
    GraphViewController.drawGraph(nodes, edges, rootNodeId);
    const layersSeparatorLinesAxises = GraphViewController.getLayersSeparatorLinesAxises(nodes);

    return (
      <GraphView
        nodes={nodes}
        edges={edges}
        rootNodeId={rootNodeId}
        readonly={readonly}
        layersSeparatorLinesAxises={layersSeparatorLinesAxises}
        foldedEdgesIds={foldedEdgesIds}
        selectNode={selectNode}
        cleanSelectedNode={cleanSelectedNode}
        addTemporaryNode={addTemporaryNode}
        cleanTemporaryNodes={cleanTemporaryNodes}
        connectButtonToDestination={connectButtonToDestination}
        toggleEdgeFoldState={this.toggleEdgeFoldState}
      />
    );
  }
}

GraphViewController.propTypes = {
  nodesData: PropTypes.array.isRequired,
  edgesData: PropTypes.array.isRequired,
  rootNodeId: PropTypes.number.isRequired,
  readonly: PropTypes.bool.isRequired,
  cleanSelectedNode: PropTypes.func.isRequired,
  selectNode: PropTypes.func.isRequired,
  connectButtonToDestination: PropTypes.func.isRequired,
  cleanTemporaryNodes: PropTypes.func.isRequired,
  addTemporaryNode: PropTypes.func.isRequired,
};

export default GraphViewController;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/containers/GraphViewController.js